import { useEffect, useRef, useContext } from 'react';
import { hasParentWithSelector } from '@mm/utils/src/dom-utils';
import { isAnyModalOpen } from '@mm/state/selectors/modalSelector';
import { PathGraphContext } from '../common/PathGraph/PathGraph';
import { useSelector } from 'react-redux';

const getMatchesHotSpot = (
  event: MouseEvent,
  refs: (HTMLDivElement | null)[],
) => {
  return !refs
    .map(ref => ref && ref.contains(event.target as Node))
    .every(match => !match);
};

type Params = {
  editMode: boolean;
  setEditMode: (editMode: boolean, event?: MouseEvent) => void;
  selectedButton: number | undefined;
  setSelectedButton: (selectedButton: number | undefined) => void;
};

export const useCloseEditModeOnNonHotSpotClick = (params: Params) => {
  const questionInputHotSpotRef = useRef<HTMLDivElement>(null);
  const questionReplyTypeHotSpotRef = useRef<HTMLDivElement>(null);
  const questionAttributeSelectorHotSpotRef = useRef<HTMLDivElement>(null);
  const processedByTriggersHotSpotRef = useRef<HTMLDivElement>(null);
  const freeFormResponsesHotSpotRef = useRef<HTMLDivElement>(null);
  const clickStartedOutsideInputHotSpotRef = useRef(false);
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const modalOpen = useSelector(isAnyModalOpen);

  const { setActiveNode } = useContext(PathGraphContext);

  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => {
      clickStartedOutsideInputHotSpotRef.current = !(
        questionInputHotSpotRef.current &&
        !questionInputHotSpotRef.current.contains(event.target as Node)
      );
    };

    const handleClick = (event: MouseEvent) => {
      const { editMode, setEditMode, setSelectedButton, selectedButton } =
        paramsRef.current;

      const matchesHotSpot = getMatchesHotSpot(event, [
        questionInputHotSpotRef.current,
        questionReplyTypeHotSpotRef.current,
        questionAttributeSelectorHotSpotRef.current,
        processedByTriggersHotSpotRef.current,
        freeFormResponsesHotSpotRef.current,
      ]);

      if (editMode) {
        if (
          !modalOpen &&
          !matchesHotSpot &&
          !hasParentWithSelector(
            event.target as Element,
            '.widget-component__title-actions',
          ) &&
          !hasParentWithSelector(
            event.target as any,
            '.react-tiny-popover-container',
          ) &&
          !hasParentWithSelector(event.target as any, '.slideout-menu') &&
          !clickStartedOutsideInputHotSpotRef.current
        ) {
          setEditMode(false, event);
        }
      } else if (matchesHotSpot) {
        if (selectedButton) {
          setSelectedButton(undefined);
        }

        if (selectedButton) {
          setActiveNode?.({
            id: selectedButton,
            type: 'button',
            action: 'close',
          });
        }
      }
    };

    window.addEventListener('mousedown', onMouseDown, true);
    window.addEventListener('click', handleClick, true);

    return () => {
      window.removeEventListener('click', handleClick, true);
      window.removeEventListener('mousedown', onMouseDown, true);
    };
  }, [modalOpen]);

  return {
    questionInputHotSpotRef,
    questionReplyTypeHotSpotRef,
    questionAttributeSelectorHotSpotRef,
    processedByTriggersHotSpotRef,
    freeFormResponsesHotSpotRef,
  };
};
